import { CommonModule } from '@angular/common';
import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { CartParametersModalConfig as CartParametersModalContentConfig } from '@front-platform/content-builder';
import { cartParametersModalResolveConfig } from '@front-platform/content-builder';
import { Component, computed, OnDestroy, OnInit, input } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { find } from 'lodash';
import { from, Observable, of, Subject } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  map,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';

import { DoodCartModel } from '@core/models/cart.model';
import { DoodDeliveryAddressModel } from '@core/models/delivery-address.model';
import { DoodMarketplaceModel } from '@core/models/marketplace.model';
import { IValidateDeliveryAddress } from '@core/models/order.model';
import { DoodShopModel } from '@core/models/shop.model';
import { ISimpleItem } from '@core/models/simple-item.model';

import {
  DistributionModeValues,
  OnSiteLocationPickModeValues,
  OrderTypeValues,
} from '@config/values/order.values';
import { ParametersWantedAtTypeValues } from '@config/values/parameters.values';

import { CartKeys } from '@config/keys/cart.keys';
import { DistributionModeKeys, ShopKeys } from '@config/keys/shop.keys';

import { IOrderTypeCapabilities } from '@config/order-types-capabilities.config';
import { ModalScrollBlockBase } from '@core/base/modalScrollBlock/modal-scroll-block.base';
import { AlertModalComponent } from '@shared/modals/alert-modal/alert-modal.component';
import { DateUtils } from '@shared/utils/date/date.utils';
import { LocationUtils } from '@shared/utils/location/location.utils';
import { ITimeSlot } from '@shared/utils/time-slots/time-slots.utils';
import { DoodOnSiteLocationSourceValues } from '@store/on-site-location/on-site-location.enum';

import { CartStoreDispatcher } from '@common/dispatchers/cart.dispatcher';
import { OnSiteLocationStoreDispatcher } from '@common/dispatchers/on-site-location.dispatcher';
import { CartStoreRefiner } from '@common/refiners/cart.refiner';
import { BasketStoreSelector } from '@common/selectors/basket.selector';
import { CartStoreSelector } from '@common/selectors/cart.selector';
import { DeliveryStoreSelector } from '@common/selectors/delivery.selector';
import { MarketplaceStoreSelector } from '@common/selectors/marketplace.selector';
import { ModalStoreSelector } from '@common/selectors/modal.selector';
import { OnSiteLocationStoreSelector } from '@common/selectors/on-site-location.selector';
import { SettingsStoreSelector } from '@common/selectors/settings.selector';
import { ShopStoreSelector } from '@common/selectors/shop.selector';

import { HttpParams } from '@angular/common/http';
import { Router } from '@angular/router';
import { OrderStoreDispatcher } from '@common/dispatchers/order.dispatcher';
import { AuthStoreSelector } from '@common/selectors/authentication.selector';
import { OrderStoreSelector } from '@common/selectors/order.selector';
import { ApiParametersKeys } from '@config/keys/parameters.keys';
import { DoodEntityModel } from '@core/models/entity.model';
import { EntitiesApiService } from '@core/services/api/entities/entities-api.service';
import { AnalyticsService } from '@core/services/app/analytics.service';
import { BasketService } from '@core/services/basket/basket.service';
import { CartService } from '@core/services/cart/cart.service';
import { DeliveryAddressService } from '@core/services/delivery-address/delivery-address.service';
import { EntitiesService } from '@core/services/entities/entities.service';
import { ErrorService } from '@core/services/error-service/error.service';
import { GetModalInterface, ModalConfig } from '@core/services/modals/modals-util.type';
import { ModalsService } from '@core/services/modals/modals.service';
import { NativeService } from '@core/services/native/native.service';
import { OnSiteLocationsService } from '@core/services/on-site-locations/on-site-locations.service';
import { OrderTypeService } from '@core/services/order-type/order-type.service';
import { OrdersService } from '@core/services/orders/orders.service';
import { RouterHelperService } from '@core/services/router-helper/router-helper.service';
import { ShopSearchParametersService } from '@core/services/shop-search-parameters/shop-search-parameters.service';
import { ShopsService } from '@core/services/shops/shops.service';
import { ModalFillerAtomComponent } from '@shared/atoms/modal-filler-atom/modal-filler-atom.component';
import { ButtonAtomComponent } from '@shared/atoms/button-atom/button-atom.component';
import { CustomSelectAtomComponent } from '@shared/atoms/custom-select-atom/custom-select-atom.component';
import { ParametersDistributionModeBlockComponent } from '@shared/blocks/parameters/parameters-distribution-mode-block/parameters-distribution-mode-block.component';
import { ParametersLocationBlockComponent } from '@shared/blocks/parameters/parameters-location-block/parameters-location-block.component';
import { ParametersDeliveryInstructionsBlockComponent } from '@shared/blocks/parameters/parameters-delivery-instructions-block/parameters-delivery-instructions-block.component';
import { ParametersWantedAtBlockComponent } from '@shared/blocks/parameters/parameters-wanted-at-block/parameters-wanted-at-block.component';
import { OnSiteLocationSelectComponent } from '@shared/components/on-site-location-select/on-site-location-select.component';

export type CartParametersModalConfig = ModalConfig<{
  handle: 'cart-parameters-modal';
  inputs: {
    existingCartMessage?: string;
  };
}>;
export interface CartParametersModalRuntimeConfig {}

export type CartParametersModalConfigInput = Partial<
  Omit<CartParametersModalContentConfig, keyof CartParametersModalRuntimeConfig> &
    CartParametersModalRuntimeConfig
>;
export type CartParametersModalResolvedConfig = Omit<
  CartParametersModalContentConfig,
  keyof CartParametersModalRuntimeConfig
> &
  Partial<CartParametersModalRuntimeConfig>;
export interface CartParametersModalContract extends ConfigComponentContract<
  CartParametersModalConfigInput,
  CartParametersModalResolvedConfig
> {}

@Component({
  selector: 'app-cart-parameters-modal',
  templateUrl: './cart-parameters-modal.component.html',
  standalone: true,
  imports: [
    CommonModule,
    TranslatePipe,
    ModalFillerAtomComponent,
    ParametersDistributionModeBlockComponent,
    CustomSelectAtomComponent,
    ParametersLocationBlockComponent,
    ButtonAtomComponent,
    ParametersDeliveryInstructionsBlockComponent,
    ParametersWantedAtBlockComponent,
    OnSiteLocationSelectComponent,
  ],
})
export class CartParametersModalComponent
  extends ModalScrollBlockBase
  implements
    OnInit,
    OnDestroy,
    GetModalInterface<CartParametersModalConfig>,
    CartParametersModalContract
{
  readonly config = input<CartParametersModalConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    CartParametersModalContentConfig,
    CartParametersModalRuntimeConfig
  >(cartParametersModalResolveConfig, () => ({ config: this.config() }));
  private _destroyerRef = new Subject<boolean>();

  static handle = 'cart-parameters-modal' as const;
  modalZIndex: Observable<number> = this.modalSelector
    .selectModal(CartParametersModalComponent.handle)
    .pipe(map(el => (el?.index ? el.index : 4)));

  readonly displayAsapForLaterPicker = computed(
    () => this.resolvedConfig()['display_asap_for_later_picker'] as unknown as boolean,
  );

  activeShop$ = this.shopSelector.selectActive$;
  isMobile$ = this.settingsSelector.selectDeviceIsMobileScreen;
  validation$ = this.orderStoreSelector.selectValidation$;

  selectedOrderTypeCapabilities?: IOrderTypeCapabilities;
  availableDistributionModes?: ISimpleItem[];
  isDeliveryAddressValid = false;
  deliveryAddressError?: string;
  isCheckDeliveryAddressInProgress = false;
  isSaving = false;
  message?: string;
  selectedDistributionMode?: string;
  selectedLocation?: DoodDeliveryAddressModel;
  selectedWantedAtType?: ParametersWantedAtTypeValues;
  selectedWantedAtDate: Date | null = null;
  selectedWantedAtTime: Date | null = null;
  wantedAtDateSlots?: Date[];
  wantedAtTimeSlots?: ITimeSlot[];
  onSiteLocationName?: string;
  onSiteLocationError?: string;
  selectedOnSiteLocationId?: string;
  private previousSelectedWantedAtTime: Date | null = null;
  isNotched = false;
  existingCartMessage = input<string | undefined>(undefined);
  orderErrorMessages: string[] = [];
  deliverableShop?: DoodEntityModel;
  deliveryComment?: string | null;
  instructions$ = this.cartSelector.selectActive$.pipe(
    map(cart => cart?.delivery_address?.instructions),
  );
  distributionMode = OrderTypeValues;
  private allWantedAtTimeSlots: ITimeSlot[] = [];
  private readonly deliveryAddressValidationDestroy$ = new Subject<void>();

  public readonly userIsLoggedIn$ = this.authSelector.selectUserIsLoggedIn;

  constructor(
    private cartRefiner: CartStoreRefiner,
    private cartSelector: CartStoreSelector,
    private shopSelector: ShopStoreSelector,
    private modalSelector: ModalStoreSelector,
    private readonly cartService: CartService,
    private basketSelector: BasketStoreSelector,
    private cartDispatcher: CartStoreDispatcher,
    private readonly shopsService: ShopsService,
    private readonly modalsService: ModalsService,
    private readonly orderDispatcher: OrderStoreDispatcher,
    private readonly orderStoreSelector: OrderStoreSelector,
    private readonly ordersService: OrdersService,
    private readonly basketService: BasketService,
    private settingsSelector: SettingsStoreSelector,
    private deliverySelector: DeliveryStoreSelector,
    private marketplaceSelector: MarketplaceStoreSelector,
    private readonly orderTypeService: OrderTypeService,
    private readonly translateService: TranslateService,
    private onSiteLocationSelector: OnSiteLocationStoreSelector,
    private onSiteLocationDispatcher: OnSiteLocationStoreDispatcher,
    private readonly onSiteLocationsService: OnSiteLocationsService,
    private readonly deliveryAddressService: DeliveryAddressService,
    private readonly shopSearchParametersService: ShopSearchParametersService,
    private readonly errorService: ErrorService,
    private readonly entitiesApiService: EntitiesApiService,
    private readonly entitiesService: EntitiesService,
    private readonly routerHelper: RouterHelperService,
    private readonly router: Router,
    private readonly analyticsService: AnalyticsService,
    private readonly authSelector: AuthStoreSelector,
  ) {
    super();
  }

  ngOnInit(): void {
    this.updateLocalFromCart();
    this.updateMessage();
    this.updateWantedAtDateSlots();
    this.loadDeliveryComment();

    from(NativeService.isNotched())
      .pipe(take(1))
      .subscribe(_isNotched => {
        this.isNotched = _isNotched;
      });

    this.loadOnSiteLocation();

    if (this.selectedDistributionMode === OrderTypeValues.Delivery) {
      this.subscribeToDeliveryAddressValidation();
    }

    this.errorService
      .getErrorMessage$()
      .pipe(
        takeUntil(this._destroyerRef),
        tap(messages => (this.orderErrorMessages = messages)),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.deliveryAddressValidationDestroy$.next();
    this.deliveryAddressValidationDestroy$.complete();
    this._destroyerRef.next(true);
    this._destroyerRef.complete();
    super.ngOnDestroy();
  }

  changeDistributionMode(distributionMode: string): void {
    this.selectedDistributionMode = distributionMode;
    this.onSiteLocationError = undefined;
    this.updateSelectedOrderTypeCapabilities();
    this.updateMessage();
    this.placeChanged(this.selectedLocation);
    this.loadOnSiteLocation(distributionMode);
    this.updateWantedAtDateSlots();

    this.deliveryAddressValidationDestroy$.next();
    if (this.selectedDistributionMode === OrderTypeValues.Delivery) {
      this.subscribeToDeliveryAddressValidation();
    }
  }

  // TODO: WTF is this ?
  placeChanged(addressValue?: Partial<DoodDeliveryAddressModel>): void {
    let address = addressValue as DoodDeliveryAddressModel;
    this.isDeliveryAddressValid = false;
    this.deliveryAddressError = undefined;
    this.deliverableShop = undefined;
    this.deliveryComment = null;

    if (
      !this.selectedOrderTypeCapabilities ||
      !this.selectedOrderTypeCapabilities.isDeliveryAddressMandatory
    ) {
      return;
    }

    if (!address) {
      const { is_default_location, location } = this.settingsSelector.parameters;
      const parametersLocation = is_default_location ? undefined : location;
      if (!parametersLocation) {
        return;
      }
      address = this.deliveryAddressService.placeResultToDeliveryAddress(
        parametersLocation,
      ) as DoodDeliveryAddressModel;
    }

    if (!this.cartService.isAddressPreciseEnough(address)) {
      this.deliveryAddressError = this.translateService.instant('parameters.add-address');
      return;
    }

    this.isCheckDeliveryAddressInProgress = true;

    this.instructions$
      .pipe(
        take(1),
        tap(instructions => {
          this.deliveryComment = instructions;
        }),
      )
      .subscribe();

    const mappedLocation = this.ordersService.mapIDeliveryAddress(address);
    this.ordersService
      .checkAddressIsValid$(this.selectedDistributionMode, mappedLocation)
      .pipe(
        take(1),
        takeUntil(this._destroyerRef),
        tap(isValid => {
          this.isDeliveryAddressValid = isValid;
          this.isCheckDeliveryAddressInProgress = false;
          if (isValid) {
            this.selectedLocation = address;
            this.deliveryAddressError = undefined;
            this.deliverableShop = undefined;

            if (this.deliveryComment !== address.instructions && address.instructions !== null) {
              this.deliveryComment = address.instructions;
            }
          }
        }),
        catchError(result => {
          this.manageDeliveryAddressError(result, address);
          return of(null);
        }),
      )
      .subscribe();
  }

  submit(): void {
    if (!this.selectedDistributionMode) {
      return;
    }

    let cart = this.cartSelector.active;
    if (!cart) {
      this.cartService.createNewCart(null, this.selectedDistributionMode);
      // TODO: Check if dispatch done before this line
      cart = this.cartSelector.active;
    }

    if (!cart) {
      console.error('Unable to create cart');
      return;
    }

    this.onSiteLocationDispatcher.updateSettings({
      id: this.selectedOnSiteLocationId,
    });

    let mappedLocation: IValidateDeliveryAddress | undefined;
    if (this.selectedLocation) {
      mappedLocation = this.ordersService.mapIDeliveryAddress(this.selectedLocation);
    } else if (this.cartSelector.active?.delivery_address) {
      mappedLocation = this.cartSelector.active?.delivery_address;
    }

    if (mappedLocation && this.deliveryComment) {
      mappedLocation.instructions = this.deliveryComment;
    }

    if (
      this.selectedOrderTypeCapabilities &&
      this.selectedOrderTypeCapabilities.isDeliveryAddressMandatory &&
      mappedLocation
    ) {
      const isAddressPreciseEnough = this.cartService.isAddressPreciseEnough({
        city: mappedLocation.city,
        street: mappedLocation.street_line_1,
        country: mappedLocation.country ?? '',
      });
      if (!isAddressPreciseEnough) {
        this.deliveryAddressError = this.translateService.instant('parameters.add-address');
        return;
      }
    }

    if (this.shouldBlockSubmitForMissingOnSiteLocation()) {
      this.onSiteLocationError = this.translateService.instant(
        'parameters.select-on-site-location',
      );
      return;
    }

    const newCart: Partial<DoodCartModel> = {
      [CartKeys.Type]: this.selectedDistributionMode,
      [CartKeys.DeliveryAddress]: mappedLocation,
      [CartKeys.WantedAt]: this.selectedWantedAtTime,
      [CartKeys.OnSiteLocationId]: this.selectedOnSiteLocationId,
      ...(this.selectedOnSiteLocationId && {
        [CartKeys.OnSiteLocationName]: this.onSiteLocationSelector.locations.find(
          ({ id }) => id === this.selectedOnSiteLocationId,
        )?.name,
      }),
      [CartKeys.IsUserSelectedDistributionMode]: true,
    };

    this.isSaving = true;
    this.cartDispatcher.updateActive(newCart);
    this.ordersService.removeOrderErrors();

    const isBasketExist = !!this.basketSelector.basket.id;
    if (isBasketExist) {
      this.basketService.updateBasket$().pipe(take(1)).subscribe();
    }

    this.orderDispatcher.checkCartIsValid();

    this.shopSearchParametersService.applyUserSelectedDistributionMode(
      this.selectedDistributionMode,
    );
    this.shopSearchParametersService.set({
      wanted_at: this.selectedWantedAtTime,
    });

    const shop = this.shopSelector.active;
    const shopOrderTypeMismatch =
      shop && shop[ShopKeys.LoadedOrderType] !== newCart?.[CartKeys.Type];
    const cartTypeChanged = newCart?.[CartKeys.Type] !== cart?.[CartKeys.Type];
    const wantedAtChanged =
      newCart?.[CartKeys.WantedAt]?.toISOString() !== cart?.[CartKeys.WantedAt]?.toISOString();

    if (shopOrderTypeMismatch || cartTypeChanged || wantedAtChanged) {
      this.reloadActiveShop$()
        .pipe(
          take(1),
          tap(() => {
            this.modalsService.close(CartParametersModalComponent.handle);
            this.isSaving = false;
          }),
        )
        .subscribe();
    } else {
      this.modalsService.close(CartParametersModalComponent.handle);
      this.isSaving = false;
    }
  }

  private subscribeToDeliveryAddressValidation(): void {
    this.deliveryAddressValidationDestroy$.next();
    this.cartRefiner.selectActiveCartDeliveryAddress
      .pipe(
        takeUntil(this._destroyerRef),
        takeUntil(this.deliveryAddressValidationDestroy$),
        distinctUntilChanged((prev, curr) => this.isSameAddressForValidation(prev, curr)),
      )
      .subscribe(address => {
        this.checkParametersDeliveryAddress(address);
      });
  }

  private isSameAddressForValidation(
    prev: IValidateDeliveryAddress | undefined,
    curr: IValidateDeliveryAddress | undefined,
  ): boolean {
    if (prev === curr) {
      return true;
    }
    if (!prev || !curr) {
      return false;
    }

    return (
      prev.street_line_1 === curr.street_line_1 &&
      prev.post_code === curr.post_code &&
      prev.city === curr.city &&
      prev.country === curr.country &&
      prev.point?.coordinates?.[0] === curr.point?.coordinates?.[0] &&
      prev.point?.coordinates?.[1] === curr.point?.coordinates?.[1]
    );
  }

  private checkParametersDeliveryAddress(
    cartDeliveryAddress: IValidateDeliveryAddress | undefined,
  ): void {
    if (this.isSaving) {
      return;
    }

    const { is_default_location, location } = this.settingsSelector.parameters;

    const deliveryAddress = this.deliverySelector.active;
    const parametersLocation = is_default_location ? undefined : (location ?? undefined);

    if (cartDeliveryAddress?.city || deliveryAddress) {
      const address = cartDeliveryAddress?.city
        ? cartDeliveryAddress
        : deliveryAddress
          ? LocationUtils.mapINewDeliveryAddressToOdDeliveryAddress(
              deliveryAddress as DoodDeliveryAddressModel,
            )
          : this.ordersService.placeResultToDeliveryAddress(parametersLocation);

      this.isCheckDeliveryAddressInProgress = true;

      this.ordersService
        .checkAddressIsValid$(this.selectedDistributionMode, address)
        .pipe(
          take(1),
          catchError(error => {
            this.manageDeliveryAddressError(error, deliveryAddress);
            return of(null);
          }),
          tap(isValid => {
            if (isValid) {
              this.isDeliveryAddressValid = isValid;
              this.deliveryAddressError = undefined;
              this.deliverableShop = undefined;
              this.isCheckDeliveryAddressInProgress = false;
            }
          }),
        )
        .subscribe();
      return;
    }

    if (!deliveryAddress && parametersLocation) {
      const mappedLocation =
        this.deliveryAddressService.placeResultToDeliveryAddress(parametersLocation);
      const mappedLocation2 = this.ordersService.mapIDeliveryAddress(mappedLocation);

      if (mappedLocation.city || mappedLocation.point?.coordinates?.length) {
        this.isCheckDeliveryAddressInProgress = true;
        this.ordersService
          .checkAddressIsValid$(this.selectedDistributionMode, mappedLocation2)
          .pipe(
            take(1),
            takeUntil(this._destroyerRef),
            tap(isValid => {
              this.isDeliveryAddressValid = isValid;
              this.isCheckDeliveryAddressInProgress = false;
            }),
            catchError(result => {
              this.manageDeliveryAddressError(result);
              return of(null);
            }),
          )
          .subscribe();
      }
    }

    if (!deliveryAddress && !parametersLocation) {
      this.isDeliveryAddressValid = false;
    }
  }

  private manageDeliveryAddressError(result: any, address?: DoodDeliveryAddressModel | null): void {
    let error = result?.error?.violations?.[0]?.code;

    switch (error) {
      case 'DELIVERY_NO_DELIVERY_ZONES_CONFIGURED':
      case 'DELIVERY_OUTSIDE_DELIVERY_ZONE':
        this.deliveryAddressError = this.translateService.instant(
          'errors.delivery-address-outside-delivery-zone',
        );
        this.analyticsService.trackErrorDisplay(
          'address_not_deliverable',
          error,
          'errors.delivery-address-outside-delivery-zone',
          'cart_parameters_modal',
        );
        if (address) {
          this.checkDeliveryAddressOnOtherShop(address);
        }
        break;
      case 'INVALID_ADDRESS_FORMAT':
      case 'DELIVERY_NO_DELIVERY_ADDRESS':
      case 'DELIVERY_INVALID_DELIVERY_ADDRESS_FORMAT':
        this.deliveryAddressError = this.translateService.instant(
          'errors.delivery-address-invalid',
        );
        this.analyticsService.trackErrorDisplay(
          'address_invalid',
          error,
          'errors.delivery-address-invalid',
          'cart_parameters_modal',
        );
        break;
      default:
        this.deliveryAddressError = this.translateService.instant('errors.unknown-error', {
          errorCode: error,
        });
        this.analyticsService.trackErrorDisplay(
          'generic_error',
          error ?? 'UNKNOWN',
          'errors.unknown-error',
          'cart_parameters_modal',
        );
    }

    this.isDeliveryAddressValid = false;
    this.isCheckDeliveryAddressInProgress = false;
  }

  private updateLocalFromCart(): void {
    const wasPromptedFromPreset =
      this.shopSearchParametersService.getParameters().is_distribution_mode_defined;

    let cart = this.cartSelector.active;
    const marketplace = this.marketplaceSelector.marketplace;

    if (!cart) {
      const modes = marketplace.distribution_modes ?? [];
      const firstDistributionMode = [...modes]
        .filter(mode => !!mode?.enabled)
        .sort((a, b) => a.priority - b.priority)[0];
      if (!firstDistributionMode?.type) {
        this.displayNoDistributionModeErrorModal();
        this.modalsService.close(CartParametersModalComponent.handle);
        return;
      }
      this.cartService.createNewCart(null, firstDistributionMode.type);
      cart = this.cartSelector.active;
    }

    if (!cart) {
      console.error('Unable to create cart');
      this.displayNoDistributionModeErrorModal();
      this.modalsService.close(CartParametersModalComponent.handle);
      return;
    }
    this.getShop(cart.shop)
      .pipe(
        takeUntil(this._destroyerRef),
        map(shop => {
          this.onSiteLocationName = cart?.[CartKeys.OnSiteLocationName];
          this.selectedOnSiteLocationId = cart?.[CartKeys.OnSiteLocationId];
          // todo check if already selected in settings & update settings accordingly?
          this.selectedDistributionMode = cart?.[CartKeys.Type];

          if (cart?.[CartKeys.DeliveryAddress]) {
            this.selectedLocation = LocationUtils.mapIValidatedDeliveryAddressToIDeliveryAddress(
              cart[CartKeys.DeliveryAddress] as IValidateDeliveryAddress,
            ) as DoodDeliveryAddressModel;
          }
          this.loadAvailableDistributionModes(marketplace, shop);
          this.updateSelectedOrderTypeCapabilities();

          if (this.userCanMakeSelection() === false && !wasPromptedFromPreset) {
            this.submit();
          }

          if (this.selectedLocation?.street) {
            this.placeChanged(this.selectedLocation);
          }

          this.initSelectedWantedAt(cart?.[CartKeys.WantedAt]);
        }),
      )
      .subscribe();
  }

  private loadAvailableDistributionModes(
    marketplace: DoodMarketplaceModel,
    shop?: DoodShopModel,
  ): void {
    this.cartService
      .getCartEntityDistributionModes$(marketplace, shop?.[ShopKeys.Id])
      .pipe(
        take(1),
        tap(availableDistributionModes => {
          const allowedDistributionModeHandles = availableDistributionModes.map(item => item.value);
          if (!allowedDistributionModeHandles.length) {
            this.displayNoDistributionModeErrorModal();
          }

          if (
            !this.selectedDistributionMode ||
            !allowedDistributionModeHandles.includes(this.selectedDistributionMode)
          ) {
            this.changeDistributionMode(availableDistributionModes[0]?.value);
          }

          this.availableDistributionModes = availableDistributionModes;
        }),
      )
      .subscribe();
  }

  private userCanMakeSelection(): boolean {
    if (this.availableDistributionModes && this.availableDistributionModes.length > 1) {
      return true;
    }

    if (this.selectedOrderTypeCapabilities?.preorderingAllowed) {
      return true;
    }

    if (this.selectedOrderTypeCapabilities?.isDeliveryAddressMandatory) {
      return true;
    }

    if (this.selectedOrderTypeCapabilities?.isOnSiteLocationMandatory) {
      return true;
    }

    return false;
  }

  private displayNoDistributionModeErrorModal(): void {
    this.modalsService.open(AlertModalComponent.handle);
    this.modalsService.setData(AlertModalComponent.handle, {
      message: this.translateService.instant('shop-page.service-not-available'),
    });
  }

  private updateSelectedOrderTypeCapabilities(): void {
    this.selectedOrderTypeCapabilities = this.orderTypeService.getCapabilities(
      this.selectedDistributionMode,
    );
  }

  private reloadActiveShop$(): Observable<DoodShopModel | null> {
    const shopSlug = this.shopSelector.active?.slug;
    if (shopSlug) {
      return this.shopsService.updateShopDistributionMode$(shopSlug);
    }

    return of(null);
  }

  onWantedAtTypeChanged(wantedAtType: ParametersWantedAtTypeValues): void {
    this.selectedWantedAtType = wantedAtType;
    if (this.selectedWantedAtType === ParametersWantedAtTypeValues.Asap) {
      this.selectedWantedAtDate = null;
      this.selectedWantedAtTime = null;
    }
    this.updateWantedAtDateSlots();
  }

  onWantedAtDateChanged(wantedAtDate?: Date | null): void {
    this.selectedWantedAtDate = wantedAtDate ?? null;
    this.previousSelectedWantedAtTime = this.selectedWantedAtTime;
    this.selectedWantedAtTime = null;
    this.updateWantedAtTimeSlots(); // local filtering only
  }

  onWantedAtTimeChanged(wantedAtTime: Date | null): void {
    this.selectedWantedAtTime = wantedAtTime;
  }

  private updateWantedAtDateSlots(): void {
    if (this.selectedWantedAtType === ParametersWantedAtTypeValues.Asap) {
      this.wantedAtDateSlots = undefined;
      this.wantedAtTimeSlots = undefined;
      this.allWantedAtTimeSlots = [];
      return;
    }

    if (!this.selectedOrderTypeCapabilities?.preorderingAllowed) {
      this.wantedAtDateSlots = undefined;
      this.wantedAtTimeSlots = undefined;
      this.allWantedAtTimeSlots = [];
      return;
    }

    this.loadAllWantedAtSlotsFromApi();
  }

  private updateWantedAtTimeSlots(): void {
    this.wantedAtTimeSlots = undefined;

    if (
      this.selectedWantedAtType === ParametersWantedAtTypeValues.Asap ||
      !this.selectedOrderTypeCapabilities?.preorderingAllowed
    ) {
      return;
    }

    if (!this.selectedWantedAtDate) {
      this.selectedWantedAtTime = null;
      return;
    }

    const selectedDay = DateUtils.dayjsInMarketplaceTimezone(this.selectedWantedAtDate);
    const daySlots = this.allWantedAtTimeSlots
      .filter(slot => slot.start.isSame(selectedDay, 'day'))
      .map((slot, idx) => ({
        ...slot,
        id: idx + 1,
      }));

    this.wantedAtTimeSlots = daySlots;

    if (!this.wantedAtTimeSlots.length) {
      this.selectedWantedAtTime = null;
      return;
    }

    this.selectedWantedAtTime = new Date(this.wantedAtTimeSlots[0].start.format());

    if (this.previousSelectedWantedAtTime) {
      const selectedSlot = find(
        this.wantedAtTimeSlots,
        slot =>
          slot.start.format('HH:mm:ss') ===
          DateUtils.dayjsInMarketplaceTimezone(this.previousSelectedWantedAtTime).format(
            'HH:mm:ss',
          ),
      );
      if (selectedSlot) {
        this.selectedWantedAtTime = new Date(selectedSlot.start.format());
      }
    }
  }

  private loadAllWantedAtSlotsFromApi(): void {
    const cart = this.cartSelector.active;

    this.wantedAtDateSlots = undefined;
    this.wantedAtTimeSlots = undefined;

    this.ordersService
      // IMPORTANT: null => fenêtre globale backend (min/max), pas "jour sélectionné"
      .getOrderSlots$(cart, null, this.selectedDistributionMode)
      .pipe(
        takeUntil(this._destroyerRef),
        take(1),
        catchError(() => {
          this.allWantedAtTimeSlots = [];
          this.wantedAtDateSlots = [];
          this.wantedAtTimeSlots = [];
          return of([]);
        }),
        tap(slots => {
          this.allWantedAtTimeSlots = this.mapUnixSlotsToTimeSlots(slots);

          const daysMap = new Map<string, Date>();
          for (const slot of this.allWantedAtTimeSlots) {
            const day = new Date(slot.start.startOf('day').format());
            const key = DateUtils.dayjsInMarketplaceTimezone(day).format('YYYY-MM-DD');
            if (!daysMap.has(key)) {
              daysMap.set(key, day);
            }
          }

          this.wantedAtDateSlots = [...daysMap.values()].sort((a, b) => a.getTime() - b.getTime());

          if (!this.wantedAtDateSlots.length) {
            this.selectedWantedAtDate = null;
            this.selectedWantedAtTime = null;
            this.wantedAtTimeSlots = [];
            return;
          }

          const selectedDateStillAvailable = this.selectedWantedAtDate
            ? this.wantedAtDateSlots.some(d =>
                DateUtils.dayjsInMarketplaceTimezone(d).isSame(this.selectedWantedAtDate, 'day'),
              )
            : false;

          if (!selectedDateStillAvailable) {
            this.selectedWantedAtDate = this.wantedAtDateSlots[0];
          }

          this.updateWantedAtTimeSlots();
        }),
      )
      .subscribe();
  }

  private mapUnixSlotsToTimeSlots(slots: number[]): ITimeSlot[] {
    return [...slots]
      .filter((slot): slot is number => typeof slot === 'number' && Number.isFinite(slot))
      .sort((a, b) => a - b)
      .map((slot, idx) => {
        const start = DateUtils.dayjsInMarketplaceTimezone(slot * 1000);
        return {
          id: idx + 1,
          start,
          end: start.add(15, 'minute'),
        };
      });
  }

  private initSelectedWantedAt(wantedAt?: Date | null): void {
    if (!wantedAt && this.displayAsapForLaterPicker()) {
      this.selectedWantedAtType = ParametersWantedAtTypeValues.Asap;
    } else {
      this.selectedWantedAtType = ParametersWantedAtTypeValues.Later;
    }

    if (wantedAt) {
      this.selectedWantedAtDate = wantedAt;
      this.previousSelectedWantedAtTime = wantedAt;
      this.selectedWantedAtTime = wantedAt;
    }
  }

  private updateMessage(): void {
    const currentDistributionMode = this.availableDistributionModes?.find(
      distributionMode => distributionMode.value === this.selectedDistributionMode,
    );
    this.message = undefined;
    if (currentDistributionMode) {
      this.message = currentDistributionMode.message;
    }
  }

  onOnSiteLocationChanged(id: string): void {
    this.onSiteLocationError = undefined;
    this.onSiteLocationDispatcher.updateSettings({
      id,
      lastLocationId: id,
      source: DoodOnSiteLocationSourceValues.fromUser,
    });
    this.onSiteLocationDispatcher.updateActive(id);
    this.selectedOnSiteLocationId = id;
  }

  private loadOnSiteLocation(_distributionMode?: string): void {
    this.cartSelector.selectActive$
      .pipe(
        take(1),
        switchMap(cart => {
          const shopId = cart?.shop;
          if (shopId) {
            return this.onSiteLocationsService.loadOnSiteLocations$(shopId);
          } else {
            return of(null);
          }
        }),
      )
      .subscribe();
  }

  private shouldBlockSubmitForMissingOnSiteLocation(): boolean {
    if (!this.selectedOrderTypeCapabilities?.isOnSiteLocationMandatory) {
      return false;
    }

    if (this.selectedOnSiteLocationId) {
      return false;
    }

    const shop = this.shopSelector.active;
    const pickMode = shop?.[ShopKeys.DistributionModes]?.find(
      dm => dm[DistributionModeKeys.Type] === DistributionModeValues.OnSite,
    )?.[DistributionModeKeys.OnsiteLocationPickMode];

    return pickMode !== OnSiteLocationPickModeValues.Url;
  }

  private getShop(shopId?: string): Observable<DoodShopModel | undefined> {
    if (shopId) {
      return this.shopSelector.selectShop(shopId).pipe(
        switchMap(_shop => {
          if (!_shop) return this.shopsService.loadShopById$(shopId);
          return of(_shop);
        }),
      );
    }
    return of(undefined);
  }

  private checkDeliveryAddressOnOtherShop(location: DoodDeliveryAddressModel) {
    let params = new HttpParams();
    params = params.append('order_type', OrderTypeValues.Delivery);
    params = params.append(ApiParametersKeys.Latitude, location.point.coordinates[1] as number);
    params = params.append(ApiParametersKeys.Longitude, location.point.coordinates[0] as number);
    params = params.append(ApiParametersKeys.MaxDistance, 15000);
    const marketplaceId = this.marketplaceSelector.marketplace.id;

    this.entitiesApiService
      .get$(marketplaceId, params)
      .subscribe(entities => (this.deliverableShop = entities[0] ?? null));
  }

  goToShop(deliverableShop: DoodEntityModel) {
    this.analyticsService.trackEvent('choose_another_shop', {
      cause: 'not-deliverable-address',
      new_shop_id: deliverableShop?.id,
      new_shop_name: deliverableShop?.name,
    });
    const shopUrl = this.entitiesService.getUrl(deliverableShop);
    this.modalsService.close(CartParametersModalComponent.handle);
    this.router.navigate([this.routerHelper.translateRoute(shopUrl)]);
  }

  private loadDeliveryComment() {
    this.cartSelector.selectActive$
      .pipe(
        take(1),
        tap(cart => {
          this.deliveryComment = cart?.delivery_address?.instructions;
        }),
      )
      .subscribe();
  }

  deliveryCommentChanged(comment: string) {
    this.deliveryComment = comment;
  }

  onAddressInputStarted(): void {
    this.deliveryAddressError = undefined;
    this.isCheckDeliveryAddressInProgress = true;
    this.ordersService.removeOrderErrors();
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(CartParametersModalComponent, { 'keyAliases': {} });
